// =============================================================================
// features/sales/campaigns/operate/useQueueSession.ts — Máquina de estados
// da fila de operação: reserva → heartbeat → resolução → próximo (doc 26
// §6.8, DoD de F35-S10).
//
// Regras de integridade que este hook garante (não são só UX):
//   - "Próximo cliente" só habilita depois que `resolved` vira `true` — e a
//     ÚNICA forma de `resolved` virar `true` é uma chamada de `release` bem
//     sucedida (resultado registrado OU devolução justificada). Nunca um
//     estado local otimista sem confirmação do servidor (doc 26 §7 "ações
//     otimistas apenas quando houver reversão segura" — aqui não há reversão
//     segura, então não é otimista).
//   - Perder a rede durante o heartbeat NUNCA apaga `member`/`lead` nem
//     bloqueia a tela — só acende `heartbeatWarning` (reversível a cada
//     tentativa seguinte). Só um 409 EXPLÍCITO do servidor (lease
//     confirmadamente perdido) vira `leaseLost`, que aí sim bloqueia
//     registrar/avançar sobre aquele item.
//   - Fechar a aba ou trocar de rota libera o item por melhor esforço
//     (`pagehide` com `keepalive` + cleanup de desmontagem) — a expiração no
//     servidor (300s) é quem garante o caso em que nem isso chega a rodar.
// =============================================================================

import type { SalesOutcomeCode } from '@elemento/shared-schemas';
import * as React from 'react';

import type {
  CampaignQueueLeadContext,
  CampaignQueueMember,
} from '../../../../hooks/sales/useCampaignQueue';
import {
  isLeaseLostError,
  QUEUE_HEARTBEAT_INTERVAL_MS,
  releaseQueueMemberBestEffort,
  useClaimNextQueue,
  useHeartbeatQueueMember,
  useReleaseQueueMember,
  useResolveQueueMember,
} from '../../../../hooks/sales/useCampaignQueue';

export type QueueSessionPhase = 'claiming' | 'empty' | 'error' | 'active';

export interface QueueSessionState {
  phase: QueueSessionPhase;
  member: CampaignQueueMember | null;
  lead: CampaignQueueLeadContext | null;
  errorMessage: string | null;
  /** `true` quando o servidor confirmou (409) que este item não é mais nosso. */
  leaseLost: boolean;
  /** `true` quando o heartbeat falhou por rede (não confirmado como perda) — some sozinho na próxima tentativa. */
  heartbeatWarning: boolean;
  /** `true` quando o item atual já foi liberado (resultado registrado ou devolvido) — habilita "Próximo cliente". */
  resolved: boolean;
  /**
   * O `member` como o servidor o devolveu na chamada que produziu `resolved`
   * — fonte da verdade do rodapé (doc 26 §12): `status='completed'` é
   * encerramento real, `status='waiting_callback'` é retorno agendado
   * (`nextActionAt` preenchido), qualquer outro valor (inclui `available`,
   * sempre o caso de `release`) volta para a fila. Nunca inferir a mensagem
   * a partir do `outcomeCode` local — só o status que o servidor confirmou.
   */
  resolvedMember: CampaignQueueMember | null;
  isReleasing: boolean;
  isResolving: boolean;
  claimNext: () => Promise<void>;
  releaseAndAdvance: (
    reason: string | undefined,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * Encerramento real (F35-S15) — chamada pelo caminho "Registrar
   * resultado". Ao contrário de `releaseAndAdvance`, propaga o erro em vez
   * de traduzi-lo para `{ ok, message }`: quem chama (RegisterContactDialog)
   * precisa distinguir um 422 de `nextActionAt` (erro de formulário) de
   * qualquer outra falha, para decidir onde mostrar o erro.
   */
  resolveAndAdvance: (
    outcomeCode: SalesOutcomeCode,
    nextActionAt: string | undefined,
  ) => Promise<void>;
}

export function useQueueSession(campaignId: string, active: boolean): QueueSessionState {
  const [phase, setPhase] = React.useState<QueueSessionPhase>('claiming');
  const [member, setMember] = React.useState<CampaignQueueMember | null>(null);
  const [lead, setLead] = React.useState<CampaignQueueLeadContext | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [leaseLost, setLeaseLost] = React.useState(false);
  const [heartbeatWarning, setHeartbeatWarning] = React.useState(false);
  const [resolved, setResolved] = React.useState(false);
  const [resolvedMember, setResolvedMember] = React.useState<CampaignQueueMember | null>(null);

  const { claimAsync } = useClaimNextQueue();
  const { releaseAsync, isPending: isReleasing } = useReleaseQueueMember();
  const { resolveAsync, isPending: isResolving } = useResolveQueueMember();
  const { heartbeatAsync } = useHeartbeatQueueMember();

  // Refs para o cleanup de desmontagem/pagehide ler o estado ATUAL sem
  // precisar recriar o listener a cada mudança (evita reanexar em todo render).
  const memberRef = React.useRef<CampaignQueueMember | null>(null);
  const resolvedRef = React.useRef(resolved);
  const leaseLostRef = React.useRef(leaseLost);
  memberRef.current = member;
  resolvedRef.current = resolved;
  leaseLostRef.current = leaseLost;

  const claimNext = React.useCallback(async (): Promise<void> => {
    setPhase('claiming');
    setErrorMessage(null);
    setLeaseLost(false);
    setHeartbeatWarning(false);
    setResolved(false);
    setResolvedMember(null);
    setMember(null);
    setLead(null);
    try {
      const result = await claimAsync(campaignId);
      if (result === null) {
        setPhase('empty');
        return;
      }
      setMember(result.member);
      setLead(result.lead);
      setPhase('active');
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível buscar o próximo cliente. Tente novamente.',
      );
      setPhase('error');
    }
  }, [campaignId, claimAsync]);

  const releaseAndAdvance = React.useCallback(
    async (reason: string | undefined): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (member === null) return { ok: false, message: 'Nenhum cliente reservado.' };
      try {
        const response = await releaseAsync({ campaignId, memberId: member.id, reason });
        setResolvedMember(response.member);
        setResolved(true);
        return { ok: true };
      } catch (error) {
        if (isLeaseLostError(error)) {
          // Já não era mais nosso — a intenção do vendedor (registrar ou
          // devolver) já está atendida do ponto de vista da fila: outro
          // agente (ou a expiração) já tirou o item das nossas mãos.
          setLeaseLost(true);
          return { ok: true };
        }
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Não foi possível liberar o item. Tente novamente — nada do que você registrou foi perdido.';
        return { ok: false, message };
      }
    },
    [campaignId, member, releaseAsync],
  );

  const resolveAndAdvance = React.useCallback(
    async (outcomeCode: SalesOutcomeCode, nextActionAt: string | undefined): Promise<void> => {
      if (member === null) throw new Error('Nenhum cliente reservado.');
      try {
        const response = await resolveAsync({
          campaignId,
          memberId: member.id,
          outcomeCode,
          nextActionAt,
        });
        setResolvedMember(response.member);
        setResolved(true);
      } catch (error) {
        if (isLeaseLostError(error)) {
          // Mesmo raciocínio de releaseAndAdvance acima: o item já não era
          // mais nosso, então a intenção de registrar já está atendida do
          // ponto de vista da fila — não é um erro para o vendedor tratar.
          setLeaseLost(true);
          return;
        }
        // Propaga (422 de nextActionAt, rede, etc.) — quem chama decide como
        // exibir. Nada aqui apaga `member`/`lead` nem o formulário do vendedor.
        throw error;
      }
    },
    [campaignId, member, resolveAsync],
  );

  // ── Claim inicial ao montar / trocar de campanha ────────────────────────
  React.useEffect(() => {
    if (!active) return;
    void claimNext();
    // Deps propositalmente restritas a campaignId/active — claimNext muda de
    // identidade a cada render (fecha sobre claimAsync), mas só deve rodar
    // aqui na 1ª montagem/troca de campanha; a UI dispara de novo explicitamente.
    // (Sem plugin react-hooks configurado neste pacote — nada a suprimir.)
  }, [campaignId, active]);

  // ── Heartbeat periódico enquanto o item está ativo e não resolvido ──────
  React.useEffect(() => {
    if (!active || phase !== 'active' || member === null || resolved || leaseLost) return undefined;

    const id = window.setInterval(() => {
      const current = memberRef.current;
      if (current === null) return;
      heartbeatAsync(campaignId, current.id)
        .then((res) => {
          setHeartbeatWarning(false);
          setMember((prev) =>
            prev !== null && prev.id === current.id
              ? { ...prev, claimExpiresAt: res.claimExpiresAt }
              : prev,
          );
        })
        .catch((error: unknown) => {
          if (isLeaseLostError(error)) {
            setLeaseLost(true);
          } else {
            setHeartbeatWarning(true);
          }
        });
    }, QUEUE_HEARTBEAT_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [active, phase, member, resolved, leaseLost, campaignId, heartbeatAsync]);

  // ── Libera por melhor esforço ao fechar a aba ou trocar de rota ─────────
  React.useEffect(() => {
    function releaseIfHolding(): void {
      const current = memberRef.current;
      if (current === null || resolvedRef.current || leaseLostRef.current) return;
      releaseQueueMemberBestEffort(campaignId, current.id);
    }

    window.addEventListener('pagehide', releaseIfHolding);
    return () => {
      window.removeEventListener('pagehide', releaseIfHolding);
      releaseIfHolding(); // desmontagem por navegação interna do SPA
    };
  }, [campaignId]);

  return {
    phase,
    member,
    lead,
    errorMessage,
    leaseLost,
    heartbeatWarning,
    resolved,
    resolvedMember,
    isReleasing,
    isResolving,
    claimNext,
    releaseAndAdvance,
    resolveAndAdvance,
  };
}
